import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3 } from 'lucide-react';
import { BULK_PHOTO_IMPORT_JOB_TYPE, importEndpoints } from '@ooh/contracts';
import type { ImportJob } from '@ooh/contracts';
import { useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { QueryBoundary } from '../../layouts/query-boundary';
import { SectionSkeleton } from '../../layouts/skeletons';
import { safeCount, computeImportedRows } from '../lib/import-stats';
import { refetchIntervalFn } from '../../lib/import-polling';

// Re-exported for unit testing (existing tests import it from this module).
export { refetchIntervalFn };

interface ProgressPhaseProps {
  importJobId: number;
  onComplete: () => void;
}

/** Returns true when the job type is a photo-bundle import. Exported for unit testing. */
export function isPhotoBundleJob(jobType: string | undefined): boolean {
  return jobType === BULK_PHOTO_IMPORT_JOB_TYPE;
}

/**
 * Pure progress calculator. Exported for unit testing.
 *
 * `totalRows` is the real denominator backfilled by the worker (image-candidate count for
 * photo bundles). When it is unknown (0/null) the percentage is 0 rather than a misleading
 * "of 1" denominator, and the percentage is capped at 100%.
 */
export function computeProgress(job: ImportJob | undefined) {
  const processedRows = safeCount(job?.processedRows);
  const totalRows = safeCount(job?.totalRows);
  const errorRows = safeCount(job?.errorRows);
  const updatedRows = safeCount(job?.updatedRows);
  const pct = totalRows > 0 ? Math.min(Math.round((processedRows / totalRows) * 100), 100) : 0;
  const importedRows = computeImportedRows(processedRows, updatedRows);
  return { processedRows, totalRows, errorRows, updatedRows, pct, importedRows };
}

export function ProgressPhase(props: ProgressPhaseProps) {
  return (
    <QueryBoundary fallback={<SectionSkeleton />}>
      <LoadedProgressPhase {...props} />
    </QueryBoundary>
  );
}

function LoadedProgressPhase({ importJobId, onComplete }: ProgressPhaseProps) {
  const { t } = useTranslation('mediaImport');

  const { data: job } = useApiSuspenseQuery(
    importEndpoints.getStatus,
    { id: importJobId },
    { refetchInterval: (data) => refetchIntervalFn(data?.status) },
  );

  const isComplete = job.status === 'completed' || job.status === 'failed';

  React.useEffect(() => {
    if (isComplete) onComplete();
  }, [isComplete, onComplete]);

  const { processedRows, totalRows, errorRows, updatedRows, pct, importedRows } =
    computeProgress(job);

  const isPhotoBundle = isPhotoBundleJob(job.type);
  const progressHeading = isPhotoBundle ? t('progress.photoBundleProgress') : t('progress.heading');

  // Photo bundles use photo-appropriate live stats (uploaded / errors) and drop the
  // always-zero "updated" row that comes from XLSX terminology.
  const statsPanel = isPhotoBundle ? (
    <div
      className="rounded-lg border border-border bg-muted/20 p-4 space-y-1.5 text-[13px]"
      data-testid="photo-progress-stats"
    >
      <div className="flex justify-between">
        <span className="text-muted-foreground">{t('progress.photoUploaded')}</span>
        <span className="font-semibold text-emerald-700" data-testid="stat-uploaded">
          {processedRows}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">{t('progress.photoNotMatchedOrErrors')}</span>
        <span className="font-semibold text-amber-700" data-testid="stat-not-matched">
          {errorRows}
        </span>
      </div>
    </div>
  ) : (
    <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-1.5 text-[13px]">
      <div className="flex justify-between">
        <span className="text-muted-foreground">{t('progress.imported')}</span>
        <span className="font-semibold text-emerald-700" data-testid="stat-imported">
          {importedRows}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">{t('progress.updated')}</span>
        <span className="font-semibold text-blue-700" data-testid="stat-updated">
          {updatedRows}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">{t('progress.errors')}</span>
        <span className="font-semibold text-red-700">{errorRows}</span>
      </div>
    </div>
  );

  return (
    <div className="max-w-xl mx-auto space-y-8 py-8" data-testid="phase-progress">
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 animate-pulse">
          <BarChart3 className="h-8 w-8 text-primary" />
        </div>
        <div>
          <p className="text-[17px] font-semibold">{progressHeading}</p>
          <p className="text-[13px] text-muted-foreground mt-1">
            {isPhotoBundle ? t('progress.photoBundleProcessed') : t('progress.processed')}{' '}
            <span className="font-semibold text-foreground" data-testid="rows-processed">
              {processedRows}
            </span>{' '}
            {t('progress.of')} <span className="font-semibold text-foreground">{totalRows}</span>{' '}
            {isPhotoBundle ? t('progress.photos') : t('progress.rows')}
          </p>
        </div>
      </div>

      <div className="space-y-2" data-testid="progress-bar-container">
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{t('progress.progressLabel')}</span>
          <span data-testid="progress-pct">{pct}%</span>
        </div>
        <div className="h-3 w-full rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all"
            style={{ width: `${pct}%` }}
            data-testid="progress-bar"
          />
        </div>
      </div>

      {statsPanel}
    </div>
  );
}
